import { BaseRepository } from "@/repositories/base-repository"
import type {
  IApprovalAllocationInput,
  ICustomerPurchaseOrder
} from "@/interfaces/api/customer-purchase-order.interface"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import DataHelper from "@/helper/data-helper"
import { AxiosError } from "axios"
import type { ICustomerInvoiceDocument } from "@/interfaces/api/customer-invoice.interface"
import type { IInvoiceReadiness } from "@/interfaces/api/customer-invoice-readiness.interface"

export class CustomerPurchaseOrderRepository extends BaseRepository<ICustomerPurchaseOrder> {
  protected resourcePath = "administration/customer-purchase-orders"

  public async invoiceReadiness(id: string): Promise<IInvoiceReadiness> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.GET,
      `${this.resourcePath}/${id}/invoice-readiness/`
    )
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as IInvoiceReadiness
  }

  public async invoiceDocument(
    id: string,
    expectedRevision?: number
  ): Promise<ICustomerInvoiceDocument> {
    const creating = expectedRevision !== undefined
    const request = new HTTPRequestBuilder(
      creating ? HTTPMethods.POST : HTTPMethods.GET,
      `${this.resourcePath}/${id}/${creating ? "create-invoice" : "invoice-document"}/`
    ).setExpectedStatusCode([200, 400, 404, 409])
    if (creating) request.setData({ expectedRevision })
    const response = await request.build().execute()
    if (response.status !== 200) {
      const messages = (value: unknown): string[] =>
        typeof value === "string"
          ? [value]
          : value && typeof value === "object"
            ? Object.values(value).flatMap(messages)
            : []
      throw new Error(messages(response.data).join(" ") || "De factuur kon niet worden gemaakt.")
    }
    return DataHelper.denormalizeData(response.data) as ICustomerInvoiceDocument
  }

  public async setDeleted(
    id: string,
    value: boolean,
    expectedRevision: number
  ): Promise<ICustomerPurchaseOrder> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/${id}/deletion/`
    )
      .setData({ value, expectedRevision })
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as ICustomerPurchaseOrder
  }

  public async setStatus(
    id: string,
    field: "processed" | "invoiced",
    value: boolean,
    expectedRevision: number
  ): Promise<ICustomerPurchaseOrder> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/${id}/status/`
    )
      .setData({ field, value, expectedRevision })
      // The card displays validation and revision errors itself.
      .setExpectedStatusCode([200, 400, 409])
      .build()
      .execute()
    if (response.status !== 200) {
      throw new AxiosError(
        `Request failed with status code ${response.status}`,
        AxiosError.ERR_BAD_REQUEST,
        response.config,
        response.request,
        response
      )
    }
    return DataHelper.denormalizeData(response.data) as ICustomerPurchaseOrder
  }

  public override async update(
    id: string,
    item: ICustomerPurchaseOrder,
    config: IRepositoryRequestConfig = {}
  ) {
    return await super.update(
      id,
      { ...item, expectedRevision: item.approvalRevision } as ICustomerPurchaseOrder,
      config
    )
  }

  public async process(
    id: string,
    allocations: IApprovalAllocationInput[],
    expectedRevision: number
  ): Promise<ICustomerPurchaseOrder> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/${id}/process/`
    )
      .setData({ allocations, expectedRevision })
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as ICustomerPurchaseOrder
  }
}
